import { Stack, Typography, Chip, Box, Button, Modal, Link } from '@mui/material';
import { ReactNode, useState } from 'react';

import { PMPRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import { formatFullDate } from '@/utils/date';

import DocumentViewer from '@/components/document/DocumentViewer';

import DashboardSection from './DashboardSection';
import DashboardSectionLoading from './DashboardSectionLoading';

export interface EventItem {
  id: number | string;
  title: string;
  description: string;
  eventDate: string;
  eventType?: string;
  source: 'database' | 'ai';
  documentId?: string;
  building?: {
    id: number;
    name: string;
  };
  unit?: {
    id: number;
    name: string;
  };
  tenant?: {
    id: number;
    full_name: string;
  };
  allDay?: boolean;
}

interface Props {
  children?: ReactNode;
  items?: EventItem[];
  isLoading?: boolean;
  title?: string;
}

const formatTime = (date: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);

const getEventTypeColor = (eventType?: string, source?: string) => {
  if (source === 'ai') {
    switch (eventType) {
    case 'lease':
      return 'primary';
    case 'bill':
      return 'warning';
    default:
      return 'info';
    }
  }
  return 'default';
};

const getEventTypeLabel = (eventType?: string, source?: string) => {
  if (source === 'ai') {
    switch (eventType) {
    case 'lease':
      return 'Lease';
    case 'bill':
      return 'Bill';
    default:
      return 'Event';
    }
  }
  return 'Calendar';
};

const DashboardEventsList: React.FC<Props> = ({ children, title = 'Events', items, isLoading }) => {
  const { session } = useAuth();

  const [documentViewOpen, setDocumentViewOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const { selectedPropertyLocation } = usePropertyManager();
  const handleViewDocument = (documentId: string) => {
    setSelectedDocumentId(documentId);
    setDocumentViewOpen(true);
  };

  const handleCloseDocumentView = () => {
    setDocumentViewOpen(false);
    setSelectedDocumentId(null);
  };

  return (
    <>
      <DashboardSection
        title={title}
        subtitle={
          !isLoading && (
            <Typography variant="dataGridTitle" color="primary" sx={{ whiteSpace: 'nowrap' }}>
              {items?.length ?? 0}{' '}
              <Typography variant="body3" component="span">
                Events
              </Typography>
            </Typography>
          )
        }
      >
        {children}
        {isLoading ? (
          <DashboardSectionLoading />
        ) : (
          <Stack spacing={2.5}>
            {items?.length ? (
              items.map(item => (
                <Box key={item.id} sx={{ p: 2, borderRadius: 1, bgcolor: 'grey.50' }}>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="subtitle3" sx={{ flex: 1 }}>
                        {item.title}
                      </Typography>
                      <Chip
                        label={getEventTypeLabel(item.eventType, item.source)}
                        color={getEventTypeColor(item.eventType, item.source)}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>

                    {item.description && (
                      <Typography variant="body2" color="textSecondary">
                        {item.description}
                      </Typography>
                    )}

                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography variant="body2" color="textPrimary" sx={{ fontWeight: 500 }}>
                        {item.allDay ? (
                          formatFullDate(new Date(item.eventDate))
                        ) : (
                          <>
                            {formatFullDate(new Date(item.eventDate))} at{' '}
                            {formatTime(new Date(item.eventDate))}
                          </>
                        )}
                      </Typography>
                    </Stack>

                    {(item.building || item.unit || item.tenant) && (
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {item.building && (
                          <Chip
                            label={`Building: ${item.building.name}`}
                            size="small"
                            variant="outlined"
                            color="default"
                          />
                        )}
                        {item.unit && (
                          <Chip
                            label={`Unit: ${item.unit.name}`}
                            size="small"
                            variant="outlined"
                            color="default"
                          />
                        )}
                        {item.tenant && (
                          <Chip
                            label={`Tenant: ${item.tenant.full_name}`}
                            size="small"
                            variant="outlined"
                            color="default"
                          />
                        )}
                      </Stack>
                    )}

                    {item.documentId && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleViewDocument(item?.documentId ?? '')}
                          sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            borderColor: '#D1D5DB',
                            color: '#374151',
                            '&:hover': {
                              borderColor: '#9CA3AF',
                              backgroundColor: '#F3F4F6',
                            },
                            fontWeight: 500,
                            px: 2,
                            py: 0.5,
                          }}
                        >
                          View Document
                        </Button>
                      </Box>
                    )}
                  </Stack>
                </Box>
              ))
            ) : (
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: 'info.lighter',
                  border: '2px solid',
                  borderColor: 'info.main',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: 'info.dark',
                    fontWeight: 600,
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <span role="img" aria-label="calendar">
                    📅
                  </span>
                  Get Started with Your Property Calendar
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  Feel free to add events to your calendar by going to the{' '}
                  <Link
                    href={PMPRoutes.calendar}
                    sx={{
                      color: 'info.dark',
                      fontWeight: 600,
                      textDecoration: 'underline',
                      '&:hover': { color: 'info.main' },
                    }}
                  >
                    Property Calendar
                  </Link>{' '}
                  page. Start uploading your documents by going to the Document Vault page or
                  forward them as email attachments to{' '}
                  <Link
                    href={`mailto:${selectedPropertyLocation?.email?.value ?? ''}`}
                    sx={{
                      color: 'info.dark',
                      fontWeight: 600,
                      textDecoration: 'underline',
                      '&:hover': { color: 'info.main' },
                    }}
                  >
                    {selectedPropertyLocation?.email?.value ?? ''}
                  </Link>
                  . As you start uploading documents, our AI will start to analyze them and power
                  your Property Calendar and start sending you reminders. You can also interact with
                  your documents by going to the{' '}
                  <Link
                    href={PMPRoutes.chatWithAI}
                    sx={{
                      color: 'info.dark',
                      fontWeight: 600,
                      textDecoration: 'underline',
                      '&:hover': { color: 'info.main' },
                    }}
                  >
                    Chat with AI
                  </Link>
                  .
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </DashboardSection>

      {/* Document View Modal */}
      <Modal
        open={documentViewOpen}
        onClose={handleCloseDocumentView}
        aria-labelledby="document-viewer-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Box
          sx={{
            width: '90vw',
            height: '90vh',
            backgroundColor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6">Document Viewer</Typography>
            <Button onClick={handleCloseDocumentView} sx={{ minWidth: 'auto', p: 1 }}>
              ×
            </Button>
          </Box>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            {selectedDocumentId && (
              <DocumentViewer id={selectedDocumentId} session={session} layoutType="modal" />
            )}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default DashboardEventsList;
